<template>
  <v-container>
    <!-- Grey-out overlay with spinner while images are uploading -->
    <v-overlay :value="uploading" absolute>
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-card class="mx-auto my-4" width="900">
      <v-card-title>Add Gallery Item</v-card-title>
      <v-card-text>
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-text-field
            label="Title"
            v-model="galleryItem.title"
            :rules="[(v) => !!v || 'Title is required']"
            required
          ></v-text-field>

          <v-text-field
            label="Venue Type"
            v-model="galleryItem.venue_type"
            :rules="[(v) => !!v || 'Venue Type is required']"
            required
          ></v-text-field>

          <v-text-field
            label="Description"
            v-model="galleryItem.description"
            :rules="[(v) => !!v || 'Value is required']"
            required
          ></v-text-field>

          <v-text-field
            label="Value"
            v-model="galleryItem.value"
            :rules="[(v) => !!v || 'Value is required']"
            type="number"
            required
          ></v-text-field>

          <v-text-field
            label="Bathroom Count"
            v-model="galleryItem.bathroom_count"
            :rules="[(v) => !!v || 'Bathroom count is required']"
            type="number"
            required
          ></v-text-field>

          <v-text-field
            label="Bedroom Count"
            v-model="galleryItem.bedroom_count"
            :rules="[(v) => !!v || 'Bedroom count is required']"
            type="number"
            required
          ></v-text-field>

          <v-text-field
            label="Lot Size"
            v-model="galleryItem.lot_size"
            :rules="[(v) => !!v || 'Lot size is required']"
            required
          ></v-text-field>

          <v-text-field
            label="File Size"
            v-model="galleryItem.file_size"
            :rules="[(v) => !!v || 'File size is required']"
            required
          ></v-text-field>

          <v-text-field
            label="File URL (optional)"
            v-model="galleryItem.file_url"
          ></v-text-field>

          <!-- Dropdown for selecting packs -->
          <v-autocomplete
            :items="packOptions"
            v-model="galleryItem.pack_ids"
            label="Select Packs"
            multiple
            chips
            item-title="name"
            item-value="id"
            :searchable="true"
          ></v-autocomplete>

          <!-- File input for uploading images -->
          <v-file-input
            label="Upload Images"
            prepend-icon="mdi-camera"
            @change="handleFileChange"
            multiple
            show-size
          ></v-file-input>

          <!-- Display uploaded image names as chips -->
          <div v-if="galleryItem.image_names.length">
            <v-chip
              v-for="(img, index) in galleryItem.image_names"
              :key="index"
              class="ma-2"
              color="primary"
              dark
            >
              {{ img }}
            </v-chip>
          </div>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          :disabled="!valid || submitting"
          color="success"
          @click="submitGalleryItem"
        >
          Submit
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import { AdminWS } from "@/services/AdminWS";
import { WS } from "@/services/WS"; // Correct way to import named export
import { useAuthStore } from "@/stores/authStore";

export default {
  name: "AddGalleryItem",
  components: {},
  data() {
    return {
      valid: false,
      submitting: false,
      uploading: false,
      galleryItem: {
        title: "",
        venue_type: "",
        value: "",
        description: "",
        bathroom_count: "",
        bedroom_count: "",
        lot_size: "",
        file_size: "",
        file_url: "",
        pack_ids: [],
        image_names: [],
      },
      packOptions: [],
      galleryItems: [],
      AuthStore: useAuthStore(),
    };
  },
  created() {
    this.loadPacks();
    this.loadGalleryItems();
  },
  methods: {
    loadPacks() {
      AdminWS.loadPacks()
        .then((packs) => {
          this.packOptions = packs;
        })
        .catch((error) => {
          console.error("Failed to load packs", error);
        });
    },
    loadGalleryItems() {
      WS.getGalleryItems()
        .then((galleryItems) => {
          this.galleryItems = galleryItems;
        })
        .catch((error) => {
          console.error("Failed to load gallery items", error);
        });
    },
    handleFileChange(filesOrEvent) {
      // If the argument is an event, extract the file list.
      let files;
      if (filesOrEvent && filesOrEvent.target && filesOrEvent.target.files) {
        files = filesOrEvent.target.files;
      } else {
        files = filesOrEvent;
      }
      console.log("Adding files:", files);
      if (!files || !files.length) {
        console.log("No files selected");
        return;
      }
      this.uploading = true;
      const uploadPromises = [];
      // Loop through selected files and upload each.
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        console.log("Uploading file", file);
        const promise = AdminWS.uploadImage(file)
          .then((uploadedFileName) => {
            // Append the returned file name to the image_names array.
            this.galleryItem.image_names.push(uploadedFileName);
            console.log("Image uploaded:", uploadedFileName);
          })
          .catch((error) => {
            console.error("Error uploading image", error);
            this.$emit("error", `Error uploading image: ${file.name}`);
          });
        uploadPromises.push(promise);
      }
      Promise.all(uploadPromises).finally(() => {
        this.uploading = false;
      });
    },
    submitGalleryItem() {
      // Validate the form first.
      if (!this.$refs.form.validate()) {
        return;
      }
      this.submitting = true;
      // Build parameters to send.
      const params = {
        title: this.galleryItem.title,
        venue_type: this.galleryItem.venue_type,
        value: this.galleryItem.value,
        bathroom_count: this.galleryItem.bathroom_count,
        bedroom_count: this.galleryItem.bedroom_count,
        description: this.galleryItem.description,
        lot_size: this.galleryItem.lot_size,
        file_size: this.galleryItem.file_size,
        file_url: this.galleryItem.file_url,
        pack_ids: this.galleryItem.pack_ids,
        image_name: this.galleryItem.image_names,
      };
      AdminWS.addGalleryItem(params)
        .then((result) => {
          alert("Gallery item added successfully!");
          console.log(result);
          this.resetForm();
          window.location.reload();
        })
        .catch((error) => {
          console.error("Error adding gallery item", error);
          alert("Error adding gallery item.");
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    resetForm() {
      this.galleryItem = {
        title: "",
        venue_type: "",
        value: "",
        bathroom_count: "",
        bedroom_count: "",
        lot_size: "",
        file_size: "",
        file_url: "",
        pack_ids: [],
        image_names: [],
      };
      if (this.$refs.form) {
        this.$refs.form.reset();
      }
    },
  },
};
</script>

<style scoped>
/* Optional custom styles */
.v-overlay {
  z-index: 2000;
}
</style>
